'use client';

import React, { useMemo, useRef } from 'react';
import type { DraggableItemResult, DroppableItemResult, Key } from 'react-aria';
import {
  mergeProps,
  useFocusVisible,
  useGridListItem,
  useHover,
} from 'react-aria';
import type { ListState } from 'react-stately';
import clsx from 'clsx';
import { useDragAndDropContext } from 'components/Collections/dnd/DragAndDropProvider';
import { DropIndicator } from 'components/Collections/dnd/DropIndicator';
import { useContextMenuTarget } from 'components/Collections/hooks/useContextMenuTarget';
import { useSize } from 'components/Collections/hooks/useSize';
import { useLink } from 'components/RouterProvider';
import { getOffsetPaddingRight } from 'components/ScrollContainer/ScrollContainer';
import { createContext, useContextSelector } from 'use-context-selector';
import { getSyntheticLinkProps } from '@react-aria/utils';
import type { ThemeSpaces } from '@frameio/vapor-theme';
import { useAnimateItem } from '../../animations';
import styles from '../../Collections.module.css';
import type { ListVariants, SetSizeWithId } from '../types';
import { ListItemDragHandleContext } from './ListItemDragHandle';
import { ignoreInputTargetElements } from './utils';

type ListBoxItemProps<T> = {
  id: Key;
  y: number;
  state: ListState<T>;
  children: React.ReactNode;
  updateItemSize: SetSizeWithId;
  height: number;
  className?: string;
  variant: ListVariants;
  gapBefore?: number;
  gapAfter?: number;
};

function ListBoxItem<T>({
  id,
  y,
  state,
  children,
  updateItemSize,
  height,
  gapBefore = 0,
  gapAfter = 0,
}: ListBoxItemProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const { collection, selectionManager } = state;

  const node = collection.getItem(id);

  useAnimateItem(ref, { y, x: node?.props?.marginX });

  useSize(
    ref,
    (size) => {
      updateItemSize(id, size);
    },
    { enabled: node?.props?.height === undefined }
  );

  /**
   * Drag and drop support
   */

  const { dragAndDropHooks, dragState, dropState } = useDragAndDropContext();

  // Initialize with a default empty object that satisfies the type
  let dragItem: DraggableItemResult = {} as DraggableItemResult;

  if (dragState && dragAndDropHooks?.useDraggableItem) {
    dragItem = dragAndDropHooks.useDraggableItem(
      { key: id, hasDragButton: true },
      dragState
    );
  }

  let dropItem: DroppableItemResult | undefined;

  if (dropState && dragAndDropHooks?.useDroppableItem) {
    dropItem = dragAndDropHooks.useDroppableItem(
      { target: { type: 'item', key: id, dropPosition: 'on' } },
      dropState,
      ref
    );
  }

  const isDragging = dragState?.isDragging(id);

  /**
   * Multi select styling info
   */

  let isLastChild = true;
  let isNextSelected = false;

  if (node?.nextKey) {
    const nextNode = collection.getItem(node?.nextKey);
    if (nextNode && nextNode?.parentKey === node?.parentKey) {
      isNextSelected = selectionManager.isSelected(nextNode.key);
      isLastChild = false;
    }
  }

  let isFirstChild = true;
  let isPrevSelected = false;

  if (node?.prevKey) {
    const prevNode = collection.getItem(node?.prevKey);
    if (prevNode && prevNode?.parentKey === node?.parentKey) {
      isPrevSelected = selectionManager.isSelected(prevNode.key);
      isFirstChild = false;
    }
  }

  const gridListItemOptions = {
    isVirtualized: true,
    node: node!,
    shouldSelectOnPressUp: !!dragState,
  };

  const {
    rowProps,
    gridCellProps,
    isPressed,
    isDisabled,
    isFocused,
    isSelected,
    allowsSelection,
    hasAction,
  } = useGridListItem(gridListItemOptions, state, ref);

  const { hoverProps, isHovered } = useHover({
    isDisabled: !hasAction && !allowsSelection,
  });

  const { contextMenuContext, contextMenuProps } = useContextMenuTarget();

  // Get dragProps from the dragItem
  let dragItemProps = {};

  if (dragItem?.dragProps) {
    // Store the original drag start handler
    const originalOnDragStart = dragItem.dragProps.onDragStart;

    // Create a modified version that restricts dragging to the handle
    // but allows direct draggable elements to work
    dragItemProps = {
      ...dragItem.dragProps,
      // eslint-disable-next-line consistent-return
      onDragStart: (e: React.DragEvent<HTMLElement>) => {
        // Find if the event target or parents have data-drag-handle
        const target = e.target as HTMLElement;
        const hasDragHandle =
          !!ref.current?.querySelector('[data-drag-handle]');
        const isDragHandleEvent = !!target.closest('[data-drag-handle]');

        // If not from handle, prevent the drag
        if (hasDragHandle && !isDragHandleEvent) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }

        // If it is from the handle, allow the drag
        if (originalOnDragStart) {
          return originalOnDragStart(e);
        }
      },
    };
  }

  // Get drag button props for keyboard accessibility
  const dragHandleProps = useMemo(() => {
    let props = {};
    if (dragItem?.dragButtonProps) {
      props = {
        ...dragItem.dragButtonProps,
        'data-drag-handle': true,
        draggable: true,
      };
    }
    return props;
  }, [dragItem?.dragButtonProps]);

  const props = mergeProps(
    {
      ...rowProps,
      onKeyDownCapture: rowProps.onKeyDownCapture
        ? ignoreInputTargetElements(rowProps.onKeyDownCapture)
        : undefined,
    },
    hoverProps,
    getSyntheticLinkProps(node?.props),
    dragItemProps,
    dropItem?.dropProps || {},
    contextMenuProps,
    {
      'data-disabled': isDisabled || undefined,
      'data-selected': isSelected || undefined,
      'data-hovered': isHovered || undefined,
      'data-focused': isFocused || undefined,
      'data-pressed': isPressed || undefined,
      'data-roving-index': selectionManager.focusedKey === id || undefined,
      'data-last-child': isLastChild || undefined,
      'data-first-child': isFirstChild || undefined,
      'data-level': node?.level,
      'data-next-selected': isNextSelected || undefined,
      'data-prev-selected': isPrevSelected || undefined,
      'data-dragging': isDragging || undefined,
      'data-allows-dragging': !!dragState || undefined,
      'data-drop-target': dropItem?.isDropTarget || undefined,
      'data-variant': node?.props?.variant,
      'data-prev-gap': gapBefore || undefined,
      'data-next-gap': gapAfter || undefined,
      'data-selection-mode': selectionManager.selectionMode,
    }
  );

  const isLink = selectionManager.isLink(id);

  const context = useMemo(() => {
    return {
      isCurrentTabStop: isFocused,
      isHovered,
      isLink,
      isSelected,
      variant: node?.props?.variant,
      contextMenuContext,
    };
  }, [
    contextMenuContext,
    isFocused,
    isHovered,
    isLink,
    isSelected,
    node?.props?.variant,
  ]);

  const Component = node?.props?.as ?? 'div';

  const { LinkComponent, linkProps } = useLink(node?.props);

  const marginRight = getOffsetPaddingRight(node?.props.marginX);

  const ItemComponent = Component;
  const LinkComponentTag = LinkComponent ?? 'a';

  return (
    <>
      {dropState && (
        <DropIndicator
          marginX={node?.props?.marginX}
          target={{ type: 'item', key: id, dropPosition: 'before' }}
          y={y - gapBefore / 2}
        />
      )}
      <ListItemDragHandleContext.Provider value={dragHandleProps}>
        <ListBoxItemContext.Provider value={context}>
          <ItemComponent
            ref={ref}
            className={clsx(
              styles.listBoxItem,
              node?.props?.className,
              isFocused &&
                document.activeElement?.tagName !== 'INPUT' &&
                'focus-border'
            )}
            data-list-item="true"
            style={{
              height: node?.props.height,
              right: `${marginRight}px`,
            }}
            {...props}
          >
            <div className={styles.gridCell} {...gridCellProps}>
              {isLink && (
                <LinkComponentTag
                  className={styles.backgroundLink}
                  {...linkProps}
                  aria-hidden="true"
                  data-react-aria-prevent-focus="true"
                  tabIndex={-1}
                />
              )}
              {children}
            </div>
          </ItemComponent>
        </ListBoxItemContext.Provider>
      </ListItemDragHandleContext.Provider>
      {dropState && isLastChild && (
        <DropIndicator
          marginX={node?.props?.marginX}
          target={{ type: 'item', key: id, dropPosition: 'after' }}
          y={y + height + gapAfter / 2}
        />
      )}
    </>
  );
}

/**
 * Context providers
 */

type ListBoxItemContextType = {
  isCurrentTabStop: boolean;
  isHovered: boolean;
  isSelected: boolean;
  isLink?: boolean;
  variant: ListVariants;
  contextMenuContext: ReturnType<
    typeof useContextMenuTarget
  >['contextMenuContext'];
};

const defaultContextMenuContext: ReturnType<
  typeof useContextMenuTarget
>['contextMenuContext'] = {
  target: null,
  setContextMenuTarget: () => {},
  registerContextMenu: () => {},
};

export const ListBoxItemContext = createContext<ListBoxItemContextType>({
  isCurrentTabStop: false,
  isHovered: false,
  isSelected: false,
  isLink: false,
  variant: 'list',
  contextMenuContext: defaultContextMenuContext,
});

export const useIsListBoxItemCurrentTabStop = () =>
  useContextSelector(ListBoxItemContext, (v) => v.isCurrentTabStop);

export const useIsListBoxItemHovered = () =>
  useContextSelector(ListBoxItemContext, (v) => v.isHovered);

export const useIsListBoxItemSelected = () =>
  useContextSelector(ListBoxItemContext, (v) => v.isSelected);

export const useListBoxItemContextMenuTarget = () =>
  useContextSelector(ListBoxItemContext, (v) => v.contextMenuContext);

export const useListBoxItemShowHoverControl = () => {
  const { isFocusVisible } = useFocusVisible();
  const isHovered = useIsListBoxItemHovered();
  const isCurrentTabStop = useIsListBoxItemCurrentTabStop();
  return isHovered || (isFocusVisible && isCurrentTabStop);
};

const LIST_BOX_ITEM_MARGIN_X = 16;
const LIST_BOX_ITEM_NAV_HEIGHT = 32;

type ListBoxItemVariants = {
  marginX: ThemeSpaces;
  height: ThemeSpaces | undefined;
};

export const listBoxItemVariants: Record<ListVariants, ListBoxItemVariants> = {
  unstyled: {
    marginX: 0,
    height: undefined,
  },
  nav: {
    marginX: LIST_BOX_ITEM_MARGIN_X,
    height: LIST_BOX_ITEM_NAV_HEIGHT,
  },
  list: {
    marginX: LIST_BOX_ITEM_MARGIN_X,
    height: undefined,
  },
};

const _ListBoxItem = React.memo(ListBoxItem);
_ListBoxItem.displayName = 'ListBoxItem';
export { _ListBoxItem as ListBoxItem };
